import { useState } from 'react'
import { generatePath, useNavigate } from 'react-router'
import { Container } from '@/components/ui/container'
import {
	Pagination,
	PaginationContent,
	PaginationItem,
	PaginationNext,
	PaginationPrevious
} from '@/components/ui/pagination'
import { FullRejectionList } from '@/features/host-review/components/full-rejection-list'
import { useGetFullRejections } from '@/features/host-review/queries/use-get-full-rejections'
import { routes } from '@/routes'
import { useTranslation } from '@/translations/i18n'

/**
 * Shoots where the host removed every photo.
 *
 * A null response means the feature is off; the page then reads as an empty
 * list rather than an error, which is the correct "nothing to see" for an
 * operator who has no host reviews running.
 */
export const HostReviewFullRejections = () => {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const [page, setPage] = useState(1)

	const { data, isLoading } = useGetFullRejections({ page })

	const items = data?.data ?? []
	const lastPage = data?.meta.lastPage ?? 1

	return (
		<Container className='flex flex-col gap-4 py-6' fullWidth>
			<div className='flex flex-col gap-1'>
				<h1 className='font-bold text-neutral-900 text-xl/7'>
					{t('host_review:full_rejections.title')}
				</h1>
				<p className='max-w-3xl text-neutral-600 text-sm/5'>
					{t('host_review:full_rejections.description')}
				</p>
			</div>

			{isLoading ? null : (
				<FullRejectionList
					items={items}
					onSelect={(reviewId) =>
						navigate(
							generatePath(
								routes.HOST_REVIEW_FULL_REJECTION_DETAIL.path,
								{ reviewId }
							)
						)
					}
				/>
			)}

			{lastPage > 1 ? (
				<Pagination>
					<PaginationContent>
						<PaginationItem>
							<PaginationPrevious
								disabled={page <= 1}
								onClick={() =>
									setPage((p) => Math.max(1, p - 1))
								}
							/>
						</PaginationItem>
						<PaginationItem>
							<PaginationNext
								disabled={page >= lastPage}
								onClick={() =>
									setPage((p) => Math.min(lastPage, p + 1))
								}
							/>
						</PaginationItem>
					</PaginationContent>
				</Pagination>
			) : null}
		</Container>
	)
}

HostReviewFullRejections.displayName = 'HostReviewFullRejections'
